import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Input, Select, Button, Switch, Row, Col, Tooltip, DatePicker } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const MAX_CONTACTS = 3;
const MAX_ARRAY_ITEMS = 2;

const WA_PHONE_TYPES = [
  { value: 'CELL', label: 'Mobile' },
  { value: 'WORK', label: 'Work' },
  { value: 'HOME', label: 'Home' },
  { value: 'MAIN', label: 'Main' },
  { value: 'IPHONE', label: 'iPhone' },
  { value: 'OTHER', label: 'Other' },
];

const EMAIL_TYPES = [
  { value: 'WORK', label: 'Work' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'OTHER', label: 'Other' },
];

const ADDRESS_TYPES = [
  { value: 'WORK', label: 'Work' },
  { value: 'HOME', label: 'Home' },
  { value: 'OTHER', label: 'Other' },
];

const URL_TYPES = [
  { value: 'WORK', label: 'Work' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'OTHER', label: 'Other' },
];

const sanitizePhoneInput = (raw) => {
  if (!raw) return '';
  // Keep only digits and plus signs
  let cleaned = raw.replace(/[^\d+]/g, '');
  // If input starts with +, keep it and remove any other pluses
  if (cleaned.startsWith('+')) {
    return '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  // If no plus at start, add one at the beginning
  return cleaned ? `+${cleaned.replace(/\+/g, '')}` : '';
};

const toWaId = (phoneWithPlus) => (phoneWithPlus || '').replace(/\D/g, '');

const formattedName = (name = {}) => {
  const first = (name.first_name || '').trim();
  const middle = (name.middle_name || '').trim();
  const last = (name.last_name || '').trim();
  const parts = [first, middle, last].filter(Boolean);
  return parts.join(' ').trim();
};

const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isValidUrl = (v) => !v || /^https?:\/\/[^\s]+$/i.test(v);

const ensureArrays = (c) => ({
  ...c,
  phones: Array.isArray(c.phones) && c.phones.length ? c.phones : [{}],
  emails: Array.isArray(c.emails) && c.emails.length ? c.emails : [{}],
  addresses: Array.isArray(c.addresses) && c.addresses.length ? c.addresses : [{}],
  urls: Array.isArray(c.urls) && c.urls.length ? c.urls : [{}],
  name: c.name || {},
  org: c.org || {},
});

const ContactMessage = ({ node, onChange }) => {
  const [contacts, setContacts] = useState([
    ensureArrays({ name: {}, phones: [{}], emails: [{}], addresses: [{}], org: {}, urls: [{}] }),
  ]);

  // Use ref to track if we should skip the next update (to prevent loops)
  const isInitializing = useRef(false);
  const lastNormalizedContacts = useRef(null);

  // Initialize contacts from node data (without breaking if missing arrays)
  useEffect(() => {
    if (node?.data?.contacts?.length > 0) {
      isInitializing.current = true;
      setContacts(node.data.contacts.map(ensureArrays));
    }
  }, [node?.data?.contacts]);

  // Normalize to WA-aligned structure but KEEP the same onChange contract (just the array)
  const normalizedContacts = useMemo(() => {
    return (contacts || []).map((c) => {
      const name = c.name || {};
      const org = c.org || {};
      const normalized = {
        addresses: (c.addresses || []).slice(0, MAX_ARRAY_ITEMS).map((a) => ({
          street: a.street || '',
          city: a.city || '',
          state: a.state || '',
          zip: a.zip || '',
          country: a.country || '',
          country_code: a.country_code || '',
          type: (a.type || 'WORK').toUpperCase(),
        })),
        birthday: c.birthday || '', // YYYY-MM-DD
        emails: (c.emails || []).slice(0, MAX_ARRAY_ITEMS).map((e) => ({
          email: e.email || '',
          type: (e.type || 'WORK').toUpperCase(),
        })),
        name: {
          formatted_name: formattedName(name),
          first_name: name.first_name || '',
          last_name: name.last_name || '',
          middle_name: name.middle_name || '',
          suffix: name.suffix || '',
          prefix: name.prefix || '',
        },
        org: {
          company: org.company || '',
          department: org.department || '',
          title: org.title || '',
        },
        phones: (c.phones || []).slice(0, MAX_ARRAY_ITEMS).map((p) => {
          const phone = sanitizePhoneInput(p.phone || '');
          const wa_id = p.wa_id ? toWaId(phone) : undefined; // only include if toggle is on
          const base = {
            phone,
            type: (p.type || 'CELL').toUpperCase(),
          };
          return wa_id ? { ...base, wa_id } : base;
        }),
        urls: (c.urls || []).slice(0, MAX_ARRAY_ITEMS).map((u) => ({
          url: u.url || '',
          type: (u.type || 'WORK').toUpperCase(),
        })),
      };
      return normalized;
    });
  }, [contacts]);

  // SOLUTION 1: Use useCallback to memoize the onChange function if possible
  // Ask your parent component to wrap onChange with useCallback
  
  // SOLUTION 2: Use deep comparison to prevent unnecessary calls
  useEffect(() => {
    // Skip if this is the initial load to prevent calling onChange with initial data
    if (isInitializing.current) {
      isInitializing.current = false;
      lastNormalizedContacts.current = normalizedContacts;
      return;
    }

    // Deep comparison to check if normalized contacts actually changed
    const hasChanged = JSON.stringify(lastNormalizedContacts.current) !== JSON.stringify(normalizedContacts);
    
    if (!hasChanged) {
      return;
    }

    lastNormalizedContacts.current = normalizedContacts;

    const payload = {
      key: 'data',
      data: {
        type: 'contacts',
        contacts: normalizedContacts,
      },
    };

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });    

  }, [normalizedContacts, node?.uniq_id]); // Removed onChange from dependencies

  const setContactField = (index, updater) => {
    setContacts((prev) => {
      const next = [...prev];
      next[index] = ensureArrays(updater(ensureArrays(next[index])));
      return next;
    });
  };

  const handleContactChange = (index, field, value, subIndex = null, subField = null) => {
    setContactField(index, (curr) => {
      const updated = { ...curr };
      if (subIndex !== null) {
        if (!updated[field]) updated[field] = [{}];
        const arr = [...updated[field]];
        arr[subIndex] = subField
          ? { ...(arr[subIndex] || {}), [subField]: value }
          : value;
        updated[field] = arr;
      } else if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[parent] = { ...(updated[parent] || {}), [child]: value };
      } else {
        updated[field] = value;
      }
      return updated;
    });
  };

  const addContact = () => {
    setContacts((prev) =>
      prev.length < MAX_CONTACTS
        ? [
            ...prev,
            ensureArrays({
              name: {},
              phones: [{}],
              emails: [{}],
              addresses: [{}],
              org: {},
              urls: [{}],
              enabled: true,
            }),
          ]
        : prev
    );
  };

  const removeContact = (index) => {
    setContacts((prev) => {
      if (prev.length <= 1) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const addFieldItem = (contactIndex, field) => {
    setContactField(contactIndex, (curr) => {
      const arr = Array.isArray(curr[field]) ? [...curr[field]] : [];
      if (arr.length < MAX_ARRAY_ITEMS) arr.push({});
      return { ...curr, [field]: arr };
    });
  };

  const removeFieldItem = (index, field, subIndex) => {
    setContactField(index, (curr) => {
      const arr = Array.isArray(curr[field]) ? [...curr[field]] : [];
      if (arr.length > 1) {
        arr.splice(subIndex, 1);
      }
      return { ...curr, [field]: arr };
    });
  };

  const renderContactCard = (contact, index) => {
    const phones = contact.phones || [{}];
    const emails = contact.emails || [{}];
    const addresses = contact.addresses || [{}];
    const urls = contact.urls || [{}];

    return (
      <div key={index} className="border rounded-lg p-4 space-y-4 mb-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">Contact {index + 1}</h4>
          {contacts.length > 1 && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeContact(index)}
              size="small"
            />
          )}
        </div>

        {/* Name Section */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Name <span className="text-red-500">*</span>
          </label>
          <Row gutter={8} className="mb-2">
            <Col span={8}>
              <Input
                placeholder="Prefix (Mr/Ms/Dr)"
                value={contact.name?.prefix || ''}
                onChange={(e) => handleContactChange(index, 'name.prefix', e.target.value)}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="First name"
                value={contact.name?.first_name || ''}
                onChange={(e) => handleContactChange(index, 'name.first_name', e.target.value)}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Middle name"
                value={contact.name?.middle_name || ''}
                onChange={(e) => handleContactChange(index, 'name.middle_name', e.target.value)}
              />
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Input
                placeholder="Last name"
                value={contact.name?.last_name || ''}
                onChange={(e) => handleContactChange(index, 'name.last_name', e.target.value)}
              />
            </Col>
            <Col span={12}>
              <Input
                placeholder="Suffix (Jr/Sr/III)"
                value={contact.name?.suffix || ''}
                onChange={(e) => handleContactChange(index, 'name.suffix', e.target.value)}
              />
            </Col>
          </Row>
        </div>

        {/* Company Section */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Organization</label>
          <Row gutter={8}>
            <Col span={8}>
              <Input
                placeholder="Company"
                value={contact.org?.company || ''}
                onChange={(e) => handleContactChange(index, 'org.company', e.target.value)}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Department"
                value={contact.org?.department || ''}
                onChange={(e) => handleContactChange(index, 'org.department', e.target.value)}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Title"
                value={contact.org?.title || ''}
                onChange={(e) => handleContactChange(index, 'org.title', e.target.value)}
              />
            </Col>
          </Row>
        </div>

        {/* Phones Section */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
              <span className="text-red-500">*</span>
            </div>
            {(phones.length || 0) < MAX_ARRAY_ITEMS && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addFieldItem(index, 'phones')}
              >
                Add Number
              </Button>
            )}
          </div>
          {phones.map((phone, phoneIndex) => {
            const hasWhatsApp = phone?.wa_id !== undefined && phone.wa_id !== null;
            return (
              <div key={phoneIndex} className="border rounded-lg p-3 space-y-3">
                <div className="flex justify-between items-center">
                  <Select
                    className="w-40"
                    value={phone.type || 'CELL'}
                    onChange={(value) =>
                      handleContactChange(index, 'phones', value, phoneIndex, 'type')
                    }
                    options={WA_PHONE_TYPES}
                  />
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {hasWhatsApp ? 'Has WhatsApp' : 'No WhatsApp'}
                      </div>
                      <Switch
                        checked={hasWhatsApp}
                        checkedChildren="WA"
                        unCheckedChildren="WA"
                        onChange={(checked) => {
                          setContacts((prev) => {
                            const next = [...prev];
                            const rec = next[index].phones[phoneIndex] || {};
                            if (checked) {
                              rec.wa_id = toWaId(rec.phone || '');
                            } else {
                              // remove wa_id key completely
                              if ('wa_id' in rec) delete rec.wa_id;
                            }
                            next[index].phones[phoneIndex] = rec;
                            return next;
                          });
                        }}
                      />
                    </div>
                    {phones.length > 1 && (
                      <Button
                        danger
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeFieldItem(index, 'phones', phoneIndex)}
                      />
                    )}
                  </div>
                </div>

                <div>
                  <Input
                    addonBefore="Phone"
                    placeholder="+919876543210"
                    value={phone.phone || ''}
                    onChange={(e) => {
                      const value = sanitizePhoneInput(e.target.value);
                      setContacts((prev) => {
                        const next = [...prev];
                        const rec = next[index].phones[phoneIndex] || {};
                        rec.phone = value;
                        if (rec.wa_id !== undefined) {
                          rec.wa_id = toWaId(value);
                        }
                        next[index].phones[phoneIndex] = rec;
                        return next;
                      });
                    }}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-1 mt-1 ml-1">
                  <div className="text-xs text-gray-500 italic">
                    • Include country code with + (e.g., +1 for US, +91 for India)
                  </div>
                  {hasWhatsApp ? (
                    <div className="text-xs text-gray-500 italic">
                      • Users will see "Message" and "Save contact" buttons
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 italic">
                      • Users will see Invite to WhatsApp button
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Emails Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">Emails</label>
            {(emails.length || 0) < MAX_ARRAY_ITEMS && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addFieldItem(index, 'emails')}
              >
                Add
              </Button>
            )}
          </div>
          {emails.map((email, emailIndex) => (
            <div key={emailIndex} className="flex gap-2 mb-2">
              <Select
                className="w-32"
                value={email.type || 'WORK'}
                onChange={(value) =>
                  handleContactChange(index, 'emails', value, emailIndex, 'type')
                }
                options={EMAIL_TYPES}
              />
              <Input
                type="email"
                placeholder="Email address"
                status={isValidEmail(email.email) ? '' : 'error'}
                value={email.email || ''}
                onChange={(e) =>
                  handleContactChange(index, 'emails', e.target.value, emailIndex, 'email')
                }
              />
              {emails.length > 1 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFieldItem(index, 'emails', emailIndex)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Addresses Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">Addresses</label>
            {(addresses.length || 0) < MAX_ARRAY_ITEMS && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addFieldItem(index, 'addresses')}
              >
                Add
              </Button>
            )}
          </div>
          {addresses.map((address, addressIndex) => (
            <div key={addressIndex} className="space-y-2 border p-2 rounded">
              <div className="flex justify-between">
                <Select
                  className="w-32"
                  value={address.type || 'WORK'}
                  onChange={(value) =>
                    handleContactChange(index, 'addresses', value, addressIndex, 'type')
                  }
                  options={ADDRESS_TYPES}
                />
                {addresses.length > 1 && (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeFieldItem(index, 'addresses', addressIndex)}
                  />
                )}
              </div>
              <Input
                placeholder="Street"
                value={address.street || ''}
                onChange={(e) =>
                  handleContactChange(index, 'addresses', e.target.value, addressIndex, 'street')
                }
              />
              <Row gutter={8}>
                <Col span={12}>
                  <Input
                    placeholder="City"
                    value={address.city || ''}
                    onChange={(e) =>
                      handleContactChange(index, 'addresses', e.target.value, addressIndex, 'city')
                    }
                  />
                </Col>
                <Col span={6}>
                  <Input
                    placeholder="State"
                    value={address.state || ''}
                    onChange={(e) =>
                      handleContactChange(index, 'addresses', e.target.value, addressIndex, 'state')
                    }
                  />
                </Col>
                <Col span={6}>
                  <Input
                    placeholder="ZIP"
                    value={address.zip || ''}
                    onChange={(e) =>
                      handleContactChange(index, 'addresses', e.target.value, addressIndex, 'zip')
                    }
                  />
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Input
                    placeholder="Country"
                    value={address.country || ''}
                    onChange={(e) =>
                      handleContactChange(index, 'addresses', e.target.value, addressIndex, 'country')
                    }
                  />
                </Col>
                <Col span={12}>
                  <Input
                    placeholder="Country code (e.g., US)"
                    value={address.country_code || ''}
                    onChange={(e) =>
                      handleContactChange(
                        index,
                        'addresses',
                        e.target.value,
                        addressIndex,
                        'country_code'
                      )
                    }
                  />
                </Col>
              </Row>
            </div>
          ))}
        </div>

        {/* URLs Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">Websites</label>
            {(urls.length || 0) < MAX_ARRAY_ITEMS && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addFieldItem(index, 'urls')}
              >
                Add
              </Button>
            )}
          </div>
          {urls.map((url, urlIndex) => (
            <div key={urlIndex} className="flex gap-2 mb-2">
              <Select
                className="w-32"
                value={url.type || 'WORK'}
                onChange={(value) => handleContactChange(index, 'urls', value, urlIndex, 'type')}
                options={URL_TYPES}
              />
              <Input
                type="url"
                placeholder="https://example.com"
                status={isValidUrl(url.url) ? '' : 'error'}
                value={url.url || ''}
                onChange={(e) => handleContactChange(index, 'urls', e.target.value, urlIndex, 'url')}
              />
              {urls.length > 1 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFieldItem(index, 'urls', urlIndex)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Birthday (AntD DatePicker so clicking input opens the calendar) */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Birthday</label>
          <DatePicker
            className="w-full"
            format="YYYY-MM-DD"
            value={contact.birthday ? dayjs(contact.birthday, 'YYYY-MM-DD') : null}
            onChange={(_, dateString) => handleContactChange(index, 'birthday', dateString)}
            placeholder="YYYY-MM-DD"
            allowClear
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {contacts.map((contact, index) => renderContactCard(contact, index))}

      {/* {contacts.length < MAX_CONTACTS && (
        <Button
          type="dashed"
          onClick={addContact}
          block
          icon={<PlusOutlined />}
          className="mt-2"
        >
          Add Contact
        </Button>
      )} */}
    </div>
  );
};

export default ContactMessage;